// Phase 9.4.B — Portal invoices API client (2026-05-11 #3).
//
// Typed wrappers around the Phase 9.4.A backend:
//   GET  /portal/invoices
//   GET  /portal/invoices/:id
//   POST /portal/invoices/:id/pay
//
// Money fields arrive as strings (Drizzle's NUMERIC default); UI formats
// via formatUsd. Aging-bucket pill class chosen via BUCKET_PILL_CLASS
// for visual urgency — current is calm, 90+ is rust (warning).
import { computeOutstandingCents, toCents } from "@merchd/shared";
import { api } from "@/lib/api";

export type InvoiceStatus =
  | "open"
  | "sent_to_client"
  | "exported"
  | "paid"
  | "voided";

// By DUE date — last delivery + terms (Stephanie 2026-10-16). `current` =
// not due yet; the rest are days past due.
export type AgingBucket = "current" | "1-30" | "31-60" | "61-90" | "90+" | "paid" | "voided";

export type InvoiceType = "deposit" | "final";

export interface PortalInvoiceListRow {
  id: string;
  invoiceNumber: number;
  orderId: string;
  orderNumber: number;
  projectName: string;
  invoiceType: InvoiceType;
  invoiceDate: string;
  status: InvoiceStatus;
  subtotal: string;
  tax: string;
  total: string;
  depositApplied: string;
  balanceDue: string;
  bucket: AgingBucket;
  paidAt: string | null;
  createdAt: string;
}

export interface PortalInvoiceListResult {
  rows: PortalInvoiceListRow[];
  nextCursor: { createdAt: string; id: string } | null;
}

export interface PortalInvoicePayment {
  id: string;
  amount: string;
  paymentMethod: string;
  status: string;
  paidAt: string | null;
}

/** One colour/size row of an invoice line — cost-free by construction on
 *  the server (no unit cost is ever selected). */
export interface PortalInvoiceLineVariant {
  id: string;
  color: string | null;
  size: string | null;
  material: string | null;
  quantity: number;
  unitPrice: string | null;
}

/** A client-visible invoice line — the same shape the client's invoice
 *  document (/i/:uuid) renders. */
export interface PortalInvoiceLine {
  id: string;
  lineNumber: number;
  serviceType: string;
  description: string | null;
  productSnapshot: {
    name?: string;
    imageUrls?: string[] | Record<string, string>;
    sku?: string;
  } | null;
  unitPrice: string;
  quantityTotal: number;
  variants: PortalInvoiceLineVariant[];
}

export interface PortalInvoiceDetail {
  id: string;
  invoiceNumber: number;
  orderId: string;
  orderNumber: number;
  projectName: string;
  invoiceType: InvoiceType;
  invoiceDate: string;
  status: InvoiceStatus;
  terms: string;
  taxCode: string | null;
  subtotal: string;
  tax: string;
  total: string;
  depositApplied: string;
  balanceDue: string;
  bucket: AgingBucket;
  billingAddressSnapshot: unknown;
  shippingAddressSnapshot: unknown;
  allowCreditCard: boolean | null;
  allowAch: boolean | null;
  sentAt: string | null;
  viewedAt: string | null;
  paidAt: string | null;
  voidedAt: string | null;
  createdAt: string;
  payments: PortalInvoicePayment[];
  lines: PortalInvoiceLine[];
}

/**
 * Portal v2 Slice A — what the client still owes on this invoice, as a
 * dollars string. THE shared formula (computeOutstandingCents: total −
 * deposit netting − succeeded ledgered payments), so this figure agrees
 * with the dashboard Action Center and with what the pay flow charges.
 * NEVER render the raw `balanceDue` column as an amount owed — it knows
 * nothing about the payments ledger, so it over-states after a part
 * payment (the D0 lesson in docs/apps/client-portal-v2.md).
 */
export function outstandingForInvoice(detail: PortalInvoiceDetail): string {
  const paidCents = detail.payments
    .filter((p) => p.status === "succeeded")
    .reduce((sum, p) => sum + toCents(p.amount), 0);
  const cents = computeOutstandingCents({
    totalCents: toCents(detail.total),
    depositAppliedCents: toCents(detail.depositApplied),
    paidCents,
  });
  return (cents / 100).toFixed(2);
}

export interface PayLinkResponse {
  url: string;
  /** True when the URL is a real Stripe Payment Link; false when the
   *  backend is running without `STRIPE_SECRET_KEY` (dev placeholder). */
  live: boolean;
}

export type UnpayableReason =
  | "already_paid"
  | "voided"
  | "no_balance"
  | "card_disabled";

// ─── Fetchers ────────────────────────────────────────────────────────

export interface ListPortalInvoicesFilters {
  status?: InvoiceStatus[];
  orderId?: string;
  limit?: number;
  cursor?: { createdAt: string; id: string } | null;
}

export async function fetchPortalInvoices(
  filters: ListPortalInvoicesFilters = {},
): Promise<PortalInvoiceListResult> {
  const params = new URLSearchParams();
  for (const s of filters.status ?? []) params.append("status", s);
  if (filters.orderId !== undefined) params.set("order_id", filters.orderId);
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  if (filters.cursor) {
    params.set("cursor_created_at", filters.cursor.createdAt);
    params.set("cursor_id", filters.cursor.id);
  }
  const qs = params.toString();
  return api<PortalInvoiceListResult>(
    `/portal/invoices${qs ? `?${qs}` : ""}`,
  );
}

export async function fetchPortalInvoice(
  id: string,
): Promise<PortalInvoiceDetail> {
  return api<PortalInvoiceDetail>(`/portal/invoices/${id}`);
}

export async function startPortalInvoicePayment(
  id: string,
): Promise<PayLinkResponse> {
  return api<PayLinkResponse>(`/portal/invoices/${id}/pay`, {
    method: "POST",
  });
}

// ─── Display helpers ─────────────────────────────────────────────────

const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export function formatUsd(amountString: string | null | undefined): string {
  if (amountString == null) return "—";
  const n = Number.parseFloat(amountString);
  if (!Number.isFinite(n)) return "—";
  return usdFormatter.format(n);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  // A bare "YYYY-MM-DD" (in-hands, invoice date) is a calendar day, not an
  // instant: new Date() reads it as UTC midnight, which shows the day BEFORE
  // anywhere west of London. Read it as local noon instead.
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`) : new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export const STATUS_LABEL: Record<InvoiceStatus, string> = {
  open: "Open",
  sent_to_client: "Sent",
  exported: "Sent",
  paid: "Paid",
  voided: "Voided",
};

/** Brand-locked pill class per status. Paid uses a calm cream tone; open
 *  uses peach to draw attention without alarm; voided fades into the
 *  background. */
export const STATUS_PILL_CLASS: Record<InvoiceStatus, string> = {
  open: "bg-peach-100 text-brown-500",
  sent_to_client: "bg-peach-100 text-brown-500",
  exported: "bg-peach-200 text-brown-500",
  paid: "bg-ok-bg text-ok",
  voided: "bg-cream-50 text-brown-300",
};

export const BUCKET_LABEL: Record<AgingBucket, string> = {
  current: "Not due yet",
  "1-30": "1–30 days past due",
  "31-60": "31–60 days past due",
  "61-90": "61–90 days past due",
  "90+": "90+ days past due",
  paid: "Paid",
  voided: "Voided",
};

/** Aging-bucket pill class. Not due = calm cream; 1–30 past due = peach;
 *  31+ = rust (visual urgency). Paid + voided fade into the background. */
export const BUCKET_PILL_CLASS: Record<AgingBucket, string> = {
  current: "bg-cream-100 text-brown-500",
  "1-30": "bg-warn-bg text-warn",
  "31-60": "bg-risk-bg text-risk",
  "61-90": "bg-risk-bg text-risk",
  "90+": "bg-risk-bg text-risk",
  paid: "bg-ok-bg text-ok",
  voided: "bg-cream-50 text-brown-300",
};

export const UNPAYABLE_REASON_LABEL: Record<UnpayableReason, string> = {
  already_paid: "This invoice is already paid.",
  voided: "This invoice has been voided.",
  no_balance: "There's no balance due on this invoice.",
  card_disabled:
    "To pay this invoice, reply to your account manager — they'll arrange payment.",
};

export const PAYMENT_METHOD_LABEL: Record<string, string> = {
  card: "Card",
  ach: "ACH",
  check: "Check",
  wire: "Wire",
  cash: "Cash",
  credit_memo: "Credit",
  other: "Other",
};
